import { IconCoins, IconDiamond, IconStar } from "@tabler/icons-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { DEFAULT_LEVEL_CAP, levelThreshold } from "../../engine/progression";
import { useGameStore } from "../../state/store";
import { ProgressBar } from "../common/ProgressBar";
import { Tooltip } from "../common/Tooltip";
import { formatMoney, formatNumber } from "../common/format";
import { cx } from "../common/cx";
import { resourceTooltip } from "../../i18n/tooltips";
import styles from "./ResourceBar.module.css";

export function ResourceBar(): JSX.Element {
  const { i18n } = useLingui();
  const player = useGameStore((state) => state.player);

  const level = player.level;
  const nextLevel = player.level + 1;
  const atMaxLevel = level >= DEFAULT_LEVEL_CAP;
  const prevThreshold = levelThreshold(level);
  const nextThreshold = levelThreshold(nextLevel);
  const xpInLevel = Math.max(0, player.xp - prevThreshold);
  const xpNeeded = Math.max(1, nextThreshold - prevThreshold);
  const xpCurrent = formatNumber(xpInLevel);
  const xpTarget = formatNumber(xpNeeded);

  return (
    <div className={styles.bar} role="group" aria-label={i18n._(msg`Ресурсы`)} data-tutorial="resource-bar">
      <Tooltip content={i18n._(resourceTooltip("xp"))}>
        <div className={styles.level}>
          <span className={cx(styles.levelPill, "mono")}>
            <IconStar size={16} aria-hidden="true" />
            <Trans>Ур. {level}</Trans>
          </span>
          {!atMaxLevel && (
            <div className={styles.xpWrap}>
              <ProgressBar
                value={xpInLevel}
                max={xpNeeded}
                context="accent"
                size="mini"
                label={i18n._(msg`Прогресс опыта`)}
              />
              <div className={styles.xpMeta}>
                <span>
                  <Trans>До ур. {nextLevel}</Trans>
                </span>
                <span className="mono">
                  <Trans>
                    {xpCurrent} / {xpTarget} XP
                  </Trans>
                </span>
              </div>
            </div>
          )}
        </div>
      </Tooltip>

      <span className={styles.spacer} />

      <Tooltip content={i18n._(resourceTooltip("money"))}>
        <div className={styles.item}>
          <IconCoins size={18} className={styles.itemIcon} aria-hidden="true" />
          <span>
            <span className={styles.label}>
              <Trans>Деньги</Trans>
            </span>
            <span className={cx(styles.value, styles.money, "mono")}>{formatMoney(player.money)}</span>
          </span>
        </div>
      </Tooltip>

      <span className={styles.divider} />

      <Tooltip content={i18n._(resourceTooltip("reputation"))}>
        <div className={styles.item}>
          <IconStar size={18} className={styles.itemIcon} aria-hidden="true" />
          <span>
            <span className={styles.label}>
              <Trans>Репутация</Trans>
            </span>
            <span className={cx(styles.value, "mono")}>{formatNumber(player.reputation)}</span>
          </span>
        </div>
      </Tooltip>

      <span className={styles.divider} />

      <Tooltip content={i18n._(resourceTooltip("skill_points"))}>
        <div className={cx(styles.item, styles.spFlag)}>
          <IconDiamond size={18} className={styles.itemIcon} aria-hidden="true" />
          <span>
            <span className={styles.label}>
              <Trans>Очки навыка</Trans>
            </span>
            <span className={cx(styles.value, "mono")}>{formatNumber(player.skillPoints)}</span>
          </span>
        </div>
      </Tooltip>
    </div>
  );
}
